import React from 'react';
import { GRADING_STYLES } from '../utils/grading';
import './ClassStandardsMatrix.css';

const compareCodes = (left, right) => String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
const byDate = (left, right) => String(left.date || '').localeCompare(String(right.date || '')) || (left.id || 0) - (right.id || 0);

export const matrixCellKey = (studentId, standardCode) => `${studentId}::${standardCode}`;

export function buildClassMatrix(rows, rosterStudents = []) {
  const students = new Map(rosterStudents.map((student) => [String(student.student_id), {
    id: String(student.student_id), name: student.label || student.name || `Student ${student.student_id}`,
  }]));
  const subjects = new Map();
  const cells = new Map();

  rows.forEach((row) => {
    const studentId = String(row.student_id);
    const code = row.standard_code || 'No standard';
    if (!students.has(studentId)) {
      students.set(studentId, { id: studentId, name: row.student_name || `Student ${studentId}` });
    }
    const subject = row.subject_name || 'Other';
    if (!subjects.has(subject)) subjects.set(subject, new Map());
    if (!subjects.get(subject).has(code)) {
      subjects.get(subject).set(code, { code, description: row.standard_description || row.cluster_expectation_description || '' });
    }
    const key = matrixCellKey(studentId, code);
    if (!cells.has(key)) cells.set(key, []);
    cells.get(key).push(row);
  });
  cells.forEach((points) => points.sort(byDate));

  return {
    students: [...students.values()].sort((left, right) => left.name.localeCompare(right.name)),
    subjects: [...subjects.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([subject, standards]) => ({ subject, standards: [...standards.values()].sort((a, b) => compareCodes(a.code, b.code)) })),
    cells,
  };
}

function pointLabel(point) {
  const status = GRADING_STYLES[point.color]?.label || 'Not assessed';
  return [point.title || 'Untitled data point', point.date, status, point.score != null ? `Score ${point.score}` : null]
    .filter(Boolean).join(' · ');
}

export default function ClassStandardsMatrix({ matrix, selectedCell, onSelectCell }: any) {
  const standards = matrix.subjects.flatMap(({ subject, standards: list }) => list.map((standard) => ({ ...standard, subject })));

  return (
    <div className="class-matrix-scroll">
      <table className="class-matrix" aria-label="Class data points by student and standard">
        <thead>
          <tr>
            <th scope="col" rowSpan={2} className="class-matrix-corner">Student</th>
            {matrix.subjects.map(({ subject, standards: list }) => (
              <th key={subject} scope="colgroup" colSpan={list.length} className="class-matrix-subject">{subject}</th>
            ))}
          </tr>
          <tr>
            {standards.map((standard) => (
              <th key={`${standard.subject}-${standard.code}`} scope="col" className="class-matrix-standard" title={standard.description || standard.code}>
                {standard.code}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.students.map((student) => (
            <tr key={student.id}>
              <th scope="row" className="class-matrix-student">{student.name}</th>
              {standards.map((standard) => {
                const key = matrixCellKey(student.id, standard.code);
                const points = matrix.cells.get(key) || [];
                const isSelected = selectedCell === key;
                return (
                  <td key={key} className={`class-matrix-cell${isSelected ? ' is-selected' : ''}`}>
                    {points.length > 0 && (
                      <button
                        type="button"
                        className="class-matrix-cell-button"
                        aria-pressed={isSelected}
                        aria-label={`${student.name}, ${standard.code}: ${points.length} data point${points.length === 1 ? '' : 's'}. Show details`}
                        onClick={() => onSelectCell(isSelected ? null : key)}
                      >
                        {points.map((point) => (
                          <span
                            key={point.id}
                            className="class-matrix-dot"
                            data-color={GRADING_STYLES[point.color] ? point.color : 'gray'}
                            title={pointLabel(point)}
                          />
                        ))}
                      </button>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
